// src/pages/payment/PaymentScreen.jsx
import React, { useState, useContext, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Alert,
  Platform,
} from "react-native";
import { AppContext } from "../../../App";
import { useTranslation } from "../../i18n";
import Header from "../../components/Header";
import api from "../../services/api"; // ✅ already added

import * as FileSystem from "expo-file-system"; // ✅ ADD
import * as Sharing from "expo-sharing"; // ✅ ADD
import { REQUIRE_PAYMENT } from "../../config/paymentConfig";

import {
  initiatePayment,
  checkPaymentStatus,
  storePaymentInfo,
  getPaymentInfo,
  getPaymentCompletionStatus,
} from "../../services/paymentService";

const COLORS = {
  primary: "#14B8A6",
  secondary: "#7C3AED",
  background: "#F7F9FA",
  card: "#FFFFFF",
  textDark: "#1C1C1E",
  textMuted: "#6B7280",
  error: "#DC2626",
  success: "#16A34A",
  warning: "#F59E0B",
  border: "#E5E7EB",
};

const API_BASE_URL = "https://api.taxi-yaounde.com";
// const API_BASE_URL = "http://localhost:4000";


export default function PaymentScreen({ navigation, route }) {
  const { verification, setVerification } = useContext(AppContext);
  const { t } = useTranslation();

  const flow = route?.params?.flow || null;
  const identifier = (route?.params?.identifier || "").toString().trim().toUpperCase();

  const routeVerificationId = route?.params?.verificationId || null;
  const routeVehicleId = route?.params?.vehicleId || null;

  const verificationId = verification?.verificationId || routeVerificationId;
  const vehicleId = verification?.vehicleId || routeVehicleId;


  const [selectedProvider, setSelectedProvider] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const [downloadingReceipt, setDownloadingReceipt] = useState(false); // ✅ ADD

  const statusCheckInterval = useRef(null);

  // const verificationId = verification?.verificationId;
  const amount = 500; // FCFA

  const fetchBackendPaymentStatus = async () => {
  if (!verificationId) return { paid: false };

  try {
    // IMPORTANT: your api instance already has baseURL, so use relative route
    const r = await api.get(`/verification/${verificationId}/payment-status`);
    const data = r?.data;

    if (data?.success && data?.isPaymentMade) {
      return { paid: true, paymentMadeAt: data?.paymentMadeAt || null };
    }
    return { paid: false };
  } catch (e) {
    console.warn("Failed to fetch backend payment status:", e?.message || e);
    return { paid: false, error: true };
  }
};


  const downloadReceipt = async () => {
    if (!verificationId) return;

    try {
      setDownloadingReceipt(true);

      const url = `${API_BASE_URL}/api/verification/${verificationId}/receipt.pdf`;

      // ✅ Web: open in new tab (browser handles download)
      if (Platform.OS === "web") {
        window.open(url, "_blank");
        return;
      }

      // ✅ Mobile: download and share/save
      const fileUri = `${FileSystem.cacheDirectory}receipt_${verificationId}.pdf`;

      const result = await FileSystem.downloadAsync(url, fileUri);

      const canShare = await Sharing.isAvailableAsync();
      if (canShare) {
        await Sharing.shareAsync(result.uri, {
          mimeType: "application/pdf",
          dialogTitle: "Download receipt",
        });
      } else {
        Alert.alert("Receipt", "Receipt downloaded, but sharing is not available on this device.");
    }
    } catch (e) {
      console.warn("Receipt download failed:", e?.message || e);
      Alert.alert(t("payment.error"), "Failed to download receipt. Please try again.");
    } finally {
      setDownloadingReceipt(false);
    }
  };

  const prestartNewRegistration = async () => {
    if (!identifier) return;

    try {
      // ✅ creates minimal vehicle + survey entry, returns verificationId + vehicleId
      const r = await api.post(`/verification/prestart-new`, { identifier });
      const d = r?.data;

      if (!d?.success || !d?.verificationId || !d?.vehicleId) {
        console.warn("Prestart failed:", d);
        return;
      }

      setVerification((prev) => ({
        ...(prev || {}),
        verificationId: d.verificationId,
        vehicleId: d.vehicleId,
        numeimma: identifier,
      }));
      // return the created ids so callers can navigate immediately
      return d;
    } catch (e) {
      console.warn("Prestart error:", e?.message || e);
    }
  };


  

  useEffect(() => {
  const init = async () => {

    // If payments are globally disabled, skip payment screens and continue.
    if (!REQUIRE_PAYMENT) {
      let vid = verification?.verificationId || route?.params?.verificationId || null;
      let vvid = verification?.vehicleId || route?.params?.vehicleId || null;

      if (flow === "NEW_REGISTRATION_PREPAY" && !vid) {
        const pre = await prestartNewRegistration();
        vid = pre?.verificationId || vid;
        vvid = pre?.vehicleId || vvid;
      }

      if (!vid) {
        console.log("❌ No verification ID found to continue without payment");
        return;
      }

      // mark verified in context and navigate exactly as `handleContinue` would
      setVerification((prev) => ({
        ...(prev || {}),
        verificationId: vid,
        vehicleId: vvid,
        payment: {
          verified: true,
          orderId: "SKIPPED",
          amount: amount,
          method: "SKIPPED",
          timestamp: new Date().toISOString(),
        },
      }));

      if (flow === "NEW_REGISTRATION_PREPAY") {
        navigation.navigate("vehicle_not_found_info", {
          identifier: identifier || verification?.numeimma,
          verificationId: vid,
          vehicleId: vvid,
          matchedDriver: route.params?.matchedDriver,
          isDriverWorkflow: route.params?.isDriverWorkflow,
        });
        return;
      }

      if (flow === "OWNER_MISMATCH_FLOW") {
        navigation.navigate("vehicle_not_matched_info", {
          identifier: identifier || verification?.numeimma,
          verificationId: vid,
          vehicleId: vvid,
          existingOwnerName: route?.params?.existingOwnerName || "",
          userEnteredOwnerInfo: route?.params?.userEnteredOwnerInfo || null,
          searchResultOwnerName: route?.params?.searchResultOwnerName || "",
          matchedDriver: route.params?.matchedDriver,
          isDriverWorkflow: route.params?.isDriverWorkflow,
        });
        return;
      }

      navigation.navigate("vehicle_admin_info", {
        matchedDriver: route.params?.matchedDriver,
        isDriverWorkflow: route.params?.isDriverWorkflow,
      });
      return;
    }

    // ✅ if coming from not-found prepay flow, ensure we have verificationId first
    if (flow === "NEW_REGISTRATION_PREPAY" && !verificationId) {
      await prestartNewRegistration();
    }

    // after prestart, re-evaluate from context
    const vid = (verification?.verificationId || route?.params?.verificationId || null);

    if (!vid) {
      console.log("❌ No verification ID found");
      return;
    }

    // ✅ 1) BACKEND FIRST (source of truth)
    const r = await api.get(`/verification/${verificationId}/payment-status`);
    const d = r.data;

    if (d?.success && d?.isPaymentMade) {
      setPaymentStatus("SUCCESSFULL");
      setPaymentInfo({
        orderId: d.paymentOrderId || "N/A",
        paymentMethod: d.paymentProvider === "MTN" ? "MOMO_CM" : "OM_CM",
        phoneNumber: d.paymentPhone || "N/A",
        timestamp: d.paymentMadeAt || new Date().toISOString(),
        status: "SUCCESSFULL",
      });

      if (statusCheckInterval.current) clearInterval(statusCheckInterval.current);
      return;
    }


    // ✅ 2) fallback to local storage + provider status logic (your existing code)
    console.log("🔍 Backend says not paid. Checking AsyncStorage...");

    const existingPayment = await getPaymentInfo(verificationId);

    if (existingPayment) {
      console.log("📋 Found existing payment:", existingPayment);

      setPaymentInfo(existingPayment);
      setPhoneNumber(existingPayment.phoneNumber);

      const provider = existingPayment.paymentMethod === "MOMO_CM" ? "MTN" : "ORANGE";
      setSelectedProvider(provider);
      setPaymentStatus(existingPayment.status);

      console.log("🔄 Verifying payment status with API...");
      const completionStatus = await getPaymentCompletionStatus(verificationId);

      if (completionStatus.completed) {
        console.log("✅ Payment already completed!");
        setPaymentStatus("SUCCESSFULL");

        if (completionStatus.paymentInfo) {
          setPaymentInfo(completionStatus.paymentInfo);
        }

        // ✅ mark payment as made in backend
        try {
          await api.put(`/verification/${verificationId}/payment-status`, { isPaymentMade: true });
        } catch (e) {
          console.warn("Failed to mark payment as made:", e?.message || e);
        }

        setVerification((prev) => ({
          ...prev,
          payment: {
            verified: true,
            orderId: completionStatus.orderId,
            amount: amount,
            method: provider,
            timestamp: new Date().toISOString(),
          },
        }));
      } else if (completionStatus.status === "PENDING") {
        console.log("⏳ Payment is still pending, starting status checking...");
        setPaymentStatus("PENDING");
        startStatusChecking(existingPayment.orderId);
      } else {
        console.log("ℹ️ Payment status:", completionStatus.status);
        setPaymentStatus(completionStatus.status);
      }
    } else {
      console.log("📝 No existing payment found for this verification");
    }
  };

  init();

  return () => {
    if (statusCheckInterval.current) {
      clearInterval(statusCheckInterval.current);
      console.log("🧹 Cleaned up status check interval");
    }
  };
}, [verificationId]);

  const startStatusChecking = (orderId) => {
    if (statusCheckInterval.current) clearInterval(statusCheckInterval.current);

    console.log("🔄 Starting status checking for order:", orderId);

    checkPaymentStatusPolling(orderId);

    statusCheckInterval.current = setInterval(async () => {
      await checkPaymentStatusPolling(orderId);
    }, 5000);
  };

  const checkPaymentStatusPolling = async (orderId) => {
    if (!orderId) return;

    try {
      console.log("🔄 Checking payment status for order:", orderId);
      setCheckingStatus(true);

      const result = await checkPaymentStatus(orderId);
      if (result.success) {
        const newStatus = result.data.status;
        console.log("📊 New payment status:", newStatus);

        if (newStatus !== paymentStatus) {
          setPaymentStatus(newStatus);

          if (paymentInfo) {
            const updatedPayment = { ...paymentInfo, status: newStatus };
            setPaymentInfo(updatedPayment);
            await storePaymentInfo(verificationId, updatedPayment);
          }

          if (newStatus === "SUCCESSFULL") {
            console.log("✅ Payment successful!");
            if (statusCheckInterval.current) clearInterval(statusCheckInterval.current);

            // ✅ use data you already have
            const phone = paymentInfo?.phoneNumber || null;
            const provider = selectedProvider || (paymentInfo?.paymentMethod === "MOMO_CM" ? "MTN" : "ORANGE") || null;

            try {
              const resp = await api.put(`/verification/${verificationId}/payment-status`, {
                isPaymentMade: true,
                paymentPhone: phone,
                paymentProvider: provider,     // "MTN" or "ORANGE"
                paymentOrderId: orderId,
                // OPTIONAL: let backend set NOW() instead of sending a date
                // paymentMadeAt: new Date().toISOString(),
              });

              // ✅ IMPORTANT: update UI from backend response (source of truth)
              const d = resp.data;

              setPaymentInfo((prev) => ({
                ...(prev || {}),
                orderId: d.paymentOrderId || orderId,
                phoneNumber: d.paymentPhone || phone || "N/A",
                paymentMethod: d.paymentProvider === "MTN" ? "MOMO_CM" : "OM_CM",
                timestamp: d.paymentMadeAt || new Date().toISOString(),
                status: "SUCCESSFULL",
              }));

            } catch (e) {
              console.warn("Failed to mark payment as made:", e?.message || e);
            }

            setPaymentStatus("SUCCESSFULL");

            Alert.alert(
              t("payment.successTitle"),
              t("payment.successMessage", { amount: amount.toLocaleString() }),
              [{ text: t("common.ok") }]
            );
          }


          if (newStatus === "FAILED" || newStatus === "CANCELLED") {
            console.log("❌ Payment failed or cancelled:", newStatus);
            if (statusCheckInterval.current) clearInterval(statusCheckInterval.current);

            Alert.alert(t("payment.error"), t("payment.paymentFailed"), [{ text: t("common.ok") }]);
          }
        }
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleProviderSelect = (provider) => setSelectedProvider(provider);

  const validatePhoneNumber = (number) => {
    const cleaned = number.replace(/\s+/g, "");
    const cameroonRegex = /^(237)?[6|2]\d{8}$/;

    if (cleaned.startsWith("237")) {
      return cleaned.length === 12 && cameroonRegex.test(cleaned);
    } else if (cleaned.startsWith("6") || cleaned.startsWith("2")) {
      return cleaned.length === 9 && cameroonRegex.test(`237${cleaned}`);
    }
    return false;
  };

  const handlePayment = async () => {
    if (!selectedProvider) {
      Alert.alert(t("payment.error"), t("payment.selectProvider"));
      return;
    }
    if (!phoneNumber.trim()) {
      Alert.alert(t("payment.error"), t("payment.enterPhoneNumber"));
      return;
    }
    if (!validatePhoneNumber(phoneNumber)) {
      Alert.alert(t("payment.error"), t("payment.invalidPhoneNumber"));
      return;
    }
    if (!verificationId) {
      Alert.alert(t("payment.error"), t("payment.noVerificationId"));
      return;
    }

    setLoading(true);

    try {
      let formattedPhone = phoneNumber.replace(/\s+/g, "");
      if (!formattedPhone.startsWith("237")) {
        formattedPhone = `237${formattedPhone}`;
      }

      const paymentMethod = selectedProvider === "MTN" ? "MOMO_CM" : "OM_CM";

      const result = await initiatePayment(formattedPhone, paymentMethod, verificationId);

      if (result.success) {
        const paymentData = {
          orderId: result.data.orderId,
          paymentToken: result.data.paymentToken,
          phoneNumber: formattedPhone,
          paymentMethod: paymentMethod,
          status: result.data.status,
          timestamp: new Date().toISOString(),
        };

        await storePaymentInfo(verificationId, paymentData);
        setPaymentInfo(paymentData);
        setPaymentStatus(result.data.status);

        console.log("🔄 Starting immediate status checking for order:", result.data.orderId);
        startStatusChecking(result.data.orderId);

        Alert.alert(
          t("payment.paymentInitiated"),
          t("payment.confirmOnPhone", { amount: amount.toLocaleString(), provider: selectedProvider }),
          [{ text: t("common.ok"), onPress: () => console.log("✅ User acknowledged payment prompt") }]
        );
      } else {
        Alert.alert(t("payment.error"), result.error || t("payment.initiationFailed"));
      }
    } catch (error) {
      console.error("Payment error:", error);
      Alert.alert(t("payment.error"), t("payment.networkError"));
    } finally {
      setLoading(false);
    }
  };

 
  const handleContinue = () => {
    if (paymentStatus === "SUCCESSFULL") {
      setVerification((prev) => ({
        ...prev,
        payment: {
          verified: true,
          orderId: paymentInfo?.orderId,
          amount: amount,
          method: selectedProvider,
          timestamp: new Date().toISOString(),
        },
      }));

      // ✅ if this was the prepay-not-found flow, go collect missing info next
      if (flow === "NEW_REGISTRATION_PREPAY") {
        navigation.navigate("vehicle_not_found_info", {
          identifier: identifier || verification?.numeimma,
          verificationId: verification?.verificationId,
          vehicleId: verification?.vehicleId,
          matchedDriver: route.params?.matchedDriver, // ✅ Forward it!
          isDriverWorkflow: route.params?.isDriverWorkflow,
        });
        return;
      }

      if (flow === "OWNER_MISMATCH_FLOW") {
        navigation.navigate("vehicle_not_matched_info", {
          identifier: identifier || verification?.numeimma,
          verificationId: verification?.verificationId,
          vehicleId: verification?.vehicleId,

          // carry over context passed from Find screen (route params)
          existingOwnerName: route?.params?.existingOwnerName || "",
          userEnteredOwnerInfo: route?.params?.userEnteredOwnerInfo || null,
          searchResultOwnerName: route?.params?.searchResultOwnerName || "",
          matchedDriver: route.params?.matchedDriver, // ✅ Forward it!
          isDriverWorkflow: route.params?.isDriverWorkflow,
        });
        return;
      }


      // ✅ normal flow
      // navigation.navigate("vehicle_admin_info");
      navigation.navigate("vehicle_admin_info", {
        // verificationId: data.verificationId,
        // vehicleId: data.vehicleId,
        matchedDriver: route.params?.matchedDriver, // ✅ Forward it!
        isDriverWorkflow: route.params?.isDriverWorkflow,
      });
    } else {
      Alert.alert(t("payment.error"), t("payment.waitForConfirmation"));
    }
  };


  const handleBack = () => navigation.navigate("find_immatriculation_or_chasis_number_screen");

  const isPhoneNumberValid = validatePhoneNumber(phoneNumber);
  const isFormValid = selectedProvider && isPhoneNumberValid && verificationId;
  const isPaymentSuccessful = paymentStatus === "SUCCESSFULL";
  const isPaymentPending = paymentStatus === "PENDING";
  const isPaymentAlreadyCompleted = paymentStatus === "SUCCESSFULL";

  return (
    <View style={styles.screen}>
      <Header />

      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <Pressable style={styles.backButton} onPress={handleBack}>
          <Text style={styles.backIcon}>←</Text>
        </Pressable>

        <View style={styles.header}>
          <Text style={styles.title}>
            {isPaymentAlreadyCompleted ? t("payment.alreadyPaidTitle") : t("payment.title")}
          </Text>
          <Text style={styles.subtitle}>
            {isPaymentAlreadyCompleted ? t("payment.alreadyPaidMessage") : t("payment.subtitle")}
          </Text>

          {!isPaymentAlreadyCompleted && (
            <View style={styles.amountCard}>
              <Text style={styles.amountLabel}>{t("payment.amount")}</Text>
              <Text style={styles.amountValue}>{amount.toLocaleString()} FCFA</Text>
            </View>
          )}
        </View>

        {!isPaymentAlreadyCompleted ? (
          <>
            {paymentStatus && (
              <View
                style={[
                  styles.statusCard,
                  isPaymentSuccessful && styles.statusCardSuccess,
                  isPaymentPending && styles.statusCardPending,
                  paymentStatus === "FAILED" && styles.statusCardError,
                ]}
              >
                <Text
                  style={[
                    styles.statusText,
                    isPaymentSuccessful && styles.statusTextSuccess,
                    isPaymentPending && styles.statusTextPending,
                    paymentStatus === "FAILED" && styles.statusTextError,
                  ]}
                >
                  {isPaymentSuccessful
                    ? t("payment.statusSuccess")
                    : isPaymentPending
                    ? t("payment.statusPending")
                    : paymentStatus === "FAILED"
                    ? t("payment.statusFailed")
                    : paymentStatus}
                </Text>

                {isPaymentPending && <Text style={styles.statusHint}>{t("payment.statusHint")}</Text>}
              </View>
            )}

            {/* Provider selection */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>{t("payment.selectProvider")}</Text>

              <View style={styles.providerContainer}>
                <Pressable
                  style={[
                    styles.providerButton,
                    selectedProvider === "MTN" && styles.providerButtonSelected,
                  ]}
                  onPress={() => handleProviderSelect("MTN")}
                >
                  <View style={styles.providerContent}>
                    <View style={[styles.providerIcon, { backgroundColor: "#FFCC00" }]}>
                      <Text style={styles.providerIconText}>M</Text>
                    </View>
                    <View style={styles.providerInfo}>
                      <Text style={styles.providerName}>MTN Mobile Money</Text>
                      <Text style={styles.providerHint}>{t("payment.mtnHint")}</Text>
                    </View>
                    {selectedProvider === "MTN" && (
                      <View style={styles.selectedIndicator}>
                        <Text style={styles.selectedIcon}>✓</Text>
                      </View>
                    )}
                  </View>
                </Pressable>

                <Pressable
                  style={[
                    styles.providerButton,
                    selectedProvider === "ORANGE" && styles.providerButtonSelected,
                  ]}
                  onPress={() => handleProviderSelect("ORANGE")}
                >
                  <View style={styles.providerContent}>
                    <View style={[styles.providerIcon, { backgroundColor: "#FF6600" }]}>
                      <Text style={styles.providerIconText}>O</Text>
                    </View>
                    <View style={styles.providerInfo}>
                      <Text style={styles.providerName}>Orange Money</Text>
                      <Text style={styles.providerHint}>{t("payment.orangeHint")}</Text>
                    </View>
                    {selectedProvider === "ORANGE" && (
                      <View style={styles.selectedIndicator}>
                        <Text style={styles.selectedIcon}>✓</Text>
                      </View>
                    )}
                  </View>
                </Pressable>
              </View>
            </View>

            {/* Phone number */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>{t("payment.phoneNumber")}</Text>
              <Text style={styles.cardHint}>{t("payment.phoneNumberHint")}</Text>

              <TextInput
                value={phoneNumber}
                onChangeText={(text) => {
                  const cleaned = text.replace(/\D/g, "");
                  let formatted = cleaned;

                  if (cleaned.length > 0) {
                    if (cleaned.startsWith("237")) {
                      formatted = `237 ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
                    } else if (cleaned.length <= 3) {
                      formatted = cleaned;
                    } else if (cleaned.length <= 6) {
                      formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
                    } else {
                      formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)}`;
                    }
                  }

                  setPhoneNumber(formatted.trim());
                }}
                placeholder="6XX XXX XXX"
                placeholderTextColor={COLORS.textMuted}
                keyboardType="phone-pad"
                maxLength={15}
                style={[
                  styles.input,
                  phoneNumber && !isPhoneNumberValid && styles.inputError,
                  phoneNumber && isPhoneNumberValid && styles.inputValid,
                ]}
              />

              {phoneNumber && !isPhoneNumberValid && (
                <Text style={styles.errorText}>{t("payment.invalidPhoneNumber")}</Text>
              )}
              {phoneNumber && isPhoneNumberValid && (
                <Text style={styles.validText}>{t("payment.validPhoneNumber")}</Text>
              )}
            </View>

            {/* Buttons */}
            {!isPaymentSuccessful ? (
              <Pressable
                onPress={handlePayment}
                disabled={!isFormValid || loading || isPaymentPending}
                style={[
                  styles.primaryButton,
                  (!isFormValid || loading || isPaymentPending) && styles.buttonDisabled,
                ]}
              >
                {loading ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : isPaymentPending ? (
                  <View style={styles.buttonContent}>
                    <ActivityIndicator color="#FFFFFF" size="small" />
                    <Text style={styles.buttonText}>{t("payment.waitingConfirmation")}</Text>
                  </View>
                ) : (
                  <Text style={styles.buttonText}>
                    {t("payment.payNow").replace("{amount}", amount.toLocaleString())}
                  </Text>
                )}
              </Pressable>
            ) : (
              <>
                {/* ✅ Success: Continue */}
                <Pressable onPress={handleContinue} style={styles.successButton}>
                  <Text style={styles.buttonText}>{t("payment.continueToNext")}</Text>
                </Pressable>

                {/* ✅ Success: Download receipt */}
                <Pressable
                  onPress={downloadReceipt}
                  disabled={downloadingReceipt}
                  style={[styles.secondaryButton, downloadingReceipt && styles.buttonDisabled]}
                >
                  {downloadingReceipt ? (
                    <ActivityIndicator color={COLORS.textDark} />
                  ) : (
                    <Text style={styles.secondaryButtonText}>Download receipt (PDF)</Text>
                  )}
                </Pressable>
              </>
            )}
          </>
        ) : (
          // Already paid
          <View style={[styles.statusCard, styles.statusCardSuccess]}>
            <Text style={[styles.statusText, styles.statusTextSuccess]}>
              ✅ {t("payment.statusSuccess")}
            </Text>
            <Text style={styles.statusHint}>
              {t("payment.successMessage").replace("{amount}", amount.toLocaleString())}
            </Text>

            {paymentInfo && (
              <View style={styles.paymentDetails}>
                <Text style={styles.detailText}>
                  Order ID: <Text style={styles.detailValue}>{paymentInfo.orderId}</Text>
                </Text>
                <Text style={styles.detailText}>
                  Paid with:{" "}
                  <Text style={styles.detailValue}>
                    {paymentInfo.paymentMethod === "MOMO_CM"
                      ? "MTN Mobile Money"
                      : "Orange Money"}
                  </Text>
                </Text>
                <Text style={styles.detailText}>
                  Phone: <Text style={styles.detailValue}>{paymentInfo.phoneNumber}</Text>
                </Text>
                <Text style={styles.detailText}>
                  Date:{" "}
                  <Text style={styles.detailValue}>
                    {new Date(paymentInfo.timestamp).toLocaleDateString('en-GB')}
                  </Text>
                </Text>
              </View>
            )}

            {/* Continue */}
            <Pressable onPress={handleContinue} style={[styles.successButton, { marginTop: 16 }]}>
              <Text style={styles.buttonText}>{t("payment.continueToNext")}</Text>
            </Pressable>

            {/* ✅ Download receipt */}
            <Pressable
              onPress={downloadReceipt}
              disabled={downloadingReceipt}
              style={[styles.secondaryButton, downloadingReceipt && styles.buttonDisabled]}
            >
              {downloadingReceipt ? (
                <ActivityIndicator color={COLORS.textDark} />
              ) : (
                <Text style={styles.secondaryButtonText}>Download receipt (PDF)</Text>
              )}
            </Pressable>
          </View>
        )}

        {!isPaymentAlreadyCompleted && (
          <View style={styles.instructionsCard}>
            <Text style={styles.instructionsTitle}>{t("payment.instructionsTitle")}</Text>
            <Text style={styles.instructionsText}>{t("payment.instructions")}</Text>
          </View>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: COLORS.background },
  scroll: { paddingHorizontal: 16, paddingBottom: 40 },
  backButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    marginTop: 16,
  },
  backIcon: { fontSize: 18, color: COLORS.textDark },
  header: { alignItems: "center", marginBottom: 20 },
  title: {
    fontSize: 24,
    fontWeight: "800",
    color: COLORS.textDark,
    textAlign: "center",
    marginBottom: 6,
  },
  subtitle: { fontSize: 14, color: COLORS.textMuted, textAlign: "center", marginBottom: 16 },
  amountCard: {
    backgroundColor: COLORS.card,
    borderRadius: 16,
    padding: 20,
    alignItems: "center",
    borderWidth: 2,
    borderColor: COLORS.primary,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    minWidth: 200,
  },
  amountLabel: { fontSize: 14, color: COLORS.textMuted, marginBottom: 4 },
  amountValue: { fontSize: 32, fontWeight: "900", color: COLORS.textDark, marginBottom: 4 },

  statusCard: {
    backgroundColor: COLORS.card,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: COLORS.border,
    alignItems: "center",
  },
  statusCardSuccess: { backgroundColor: "rgba(22, 163, 74, 0.1)", borderColor: "rgba(22, 163, 74, 0.3)" },
  statusCardPending: { backgroundColor: "rgba(245, 158, 11, 0.1)", borderColor: "rgba(245, 158, 11, 0.3)" },
  statusCardError: { backgroundColor: "rgba(220, 38, 38, 0.1)", borderColor: "rgba(220, 38, 38, 0.3)" },

  statusText: { fontSize: 16, fontWeight: "700", color: COLORS.textDark },
  statusTextSuccess: { color: COLORS.success },
  statusTextPending: { color: COLORS.warning },
  statusTextError: { color: COLORS.error },
  statusHint: { fontSize: 12, color: COLORS.textMuted, marginTop: 4, textAlign: "center" },

  card: {
    backgroundColor: COLORS.card,
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  cardTitle: { fontSize: 16, fontWeight: "700", color: COLORS.textDark, marginBottom: 4 },
  cardHint: { fontSize: 13, color: COLORS.textMuted, marginBottom: 12 },

  providerContainer: { gap: 8 },
  providerButton: { backgroundColor: "#F9FAFB", borderRadius: 12, padding: 12, borderWidth: 2, borderColor: "transparent" },
  providerButtonSelected: { backgroundColor: "#F0F9FF", borderColor: COLORS.primary },
  providerContent: { flexDirection: "row", alignItems: "center", gap: 12 },
  providerIcon: { width: 40, height: 40, borderRadius: 20, alignItems: "center", justifyContent: "center" },
  providerIconText: { color: "#FFFFFF", fontSize: 18, fontWeight: "800" },
  providerInfo: { flex: 1 },
  providerName: { fontSize: 15, fontWeight: "700", color: COLORS.textDark, marginBottom: 2 },
  providerHint: { fontSize: 12, color: COLORS.textMuted },
  selectedIndicator: { width: 24, height: 24, borderRadius: 12, backgroundColor: COLORS.primary, alignItems: "center", justifyContent: "center" },
  selectedIcon: { color: "#FFFFFF", fontSize: 14, fontWeight: "bold" },

  input: { height: 48, borderRadius: 12, backgroundColor: "#F9FAFB", borderWidth: 1, borderColor: COLORS.border, paddingHorizontal: 12, fontSize: 16, color: COLORS.textDark, marginTop: 8 },
  inputError: { borderColor: COLORS.error, backgroundColor: "#FEF2F2" },
  inputValid: { borderColor: COLORS.success, backgroundColor: "#F0FDF4" },

  errorText: { color: COLORS.error, fontSize: 12, marginTop: 4, marginLeft: 2 },
  validText: { color: COLORS.success, fontSize: 12, marginTop: 4, marginLeft: 2 },

  primaryButton: { backgroundColor: COLORS.primary, paddingVertical: 16, paddingHorizontal: 20, borderRadius: 14, alignItems: "center", marginTop: 16, shadowColor: COLORS.primary, shadowOpacity: 0.2, shadowRadius: 8, shadowOffset: { width: 0, height: 4 }, elevation: 4 },
  successButton: { backgroundColor: COLORS.success, paddingVertical: 16, paddingHorizontal: 20, borderRadius: 14, alignItems: "center", marginTop: 16, shadowColor: COLORS.success, shadowOpacity: 0.2, shadowRadius: 8, shadowOffset: { width: 0, height: 4 }, elevation: 4 },

  // ✅ New "Download receipt" button style
  secondaryButton: {
    backgroundColor: "#F3F4F6",
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 14,
    alignItems: "center",
    marginTop: 10,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  secondaryButtonText: {
    color: COLORS.textDark,
    fontSize: 15,
    fontWeight: "700",
  },

  buttonDisabled: { opacity: 0.5 },
  buttonContent: { flexDirection: "row", alignItems: "center", gap: 8 },
  buttonText: { color: "#FFFFFF", fontSize: 16, fontWeight: "700" },

  instructionsCard: { backgroundColor: "#FFF7ED", borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: "#FDBA74" },
  instructionsTitle: { fontSize: 14, fontWeight: "700", color: "#C2410C", marginBottom: 8 },
  instructionsText: { fontSize: 13, color: "#92400E", lineHeight: 18 },

  paymentDetails: { marginTop: 12, padding: 12, backgroundColor: "rgba(255, 255, 255, 0.3)", borderRadius: 8, width: "100%" },
  detailText: { fontSize: 12, color: COLORS.textDark, marginBottom: 4 },
  detailValue: { fontWeight: "600", fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace" },
});

